import classNames from 'classnames';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Button, Label, Radio, TextInput } from 'flowbite-react';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { HiClock, HiLocationMarker } from 'react-icons/hi';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import { IoMdAdd, IoMdRemove } from 'react-icons/io';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { MultiValue, SingleValue } from 'react-select';
import {
	camelCaseToSpace,
	designationMappingWithTechStack,
	Dropdown,
	generatePositionTitle,
	generateTechStackName,
	locationPreferenceOptions,
} from 'shared-utils';
import { registerForDrive } from 'src/apis/drive';
import CompanySelectionWithId from 'src/components/Auth/CompanySelectionWithId';
import { PrettoSlider } from 'src/components/university/UniversityStudentFilters';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetAllDrivesQuery } from 'src/hooks/drives.hooks';
import { useGetAllSkills } from 'src/hooks/skill.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import {
	CompanyDocument,
	PositionTitle,
	RecruiterFormData,
	SkillType,
	UserCareerPathType,
	UserRole,
	WorkingMode,
} from 'types';

dayjs.extend(advancedFormat);

const DriveFormForRecruiter = ({
	isModal = false,
	onFinish,
	existingPositionData,
	onSubmit,
}: {
	isModal?: boolean;
	onFinish?: (formData: RecruiterFormData) => void;
	existingPositionData?: RecruiterFormData;
	onSubmit?: (formData: RecruiterFormData) => void;
}) => {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user, isAccountManagerLoggedIn, isRecruiterLoggedIn } = useUserContext();
	const [searchParams, setSearchParams] = useSearchParams();

	const { data: skillsData } = useGetAllSkills({
		type: SkillType.SKILL,
	});
	const { data: positionTitlesData } = useGetDropdownItemsByCategory('POSITION_TITLE', undefined, true);

	const skillOptions = skillsData?.skills
		?.filter(skill => skill?.skillGroupType)
		?.map(skill => ({ value: skill?._id, label: skill?.name?.replaceAll('_', ' ') }));

	const drives: string[] = JSON.parse(searchParams.get('drive') || '[]');
	const { data, isLoading, refetch } = useGetAllDrivesQuery();
	const drive = data?.drives?.find(
		item => drives?.includes(item?._id),
		// && item?.isRegistered === false
	);
	const [techStacks, setTechStacks] = useState<Dropdown[]>([]);
	const [positionTitles, setPositionTitles] = useState<Dropdown[]>([]);
	const [isSticky, setIsSticky] = useState(false);

	const _limits = { top: 0, bottom: 130 }; // Define your limits here
	const STATES = { Static: 'static', Fixed: 'fixed', Stuck: 'stuck' }; // Define your states here

	const _calcState = (windowOffset: number) => {
		if (windowOffset < _limits.top) {
			return STATES.Static; // Return Static if above the top limit
		} else if (windowOffset >= _limits.bottom) {
			return STATES.Stuck; // Return Stuck if below the bottom limit
		}
		return STATES.Fixed; // Return Fixed if within limits
	};

	useEffect(() => {
		const handleScroll = () => {
			const windowOffset = window.scrollY; // Get the current scroll position
			const state = _calcState(windowOffset); // Call _calcState with the current scroll position
			setIsSticky(state === STATES.Stuck); // Update isSticky based on the state
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	useEffect(() => {
		if (existingPositionData || onSubmit) {
			return;
		}
		if (!drive && !isLoading && !isModal) {
			navigate('/');
		}
	}, [drive, isLoading, isModal]);

	useEffect(() => {
		if (existingPositionData) {
			setFormData(prev => ({
				...prev,
				...existingPositionData,
				companyLocation: existingPositionData.companyLocation || [],
				internshipDuration: (existingPositionData as any)?.internshipDuration || 0,
				internshipSalary: (existingPositionData as any)?.internshipSalary || { min: 5, max: 10 },
			}));
		}
	}, [existingPositionData]);

	const registerForDriveFn = useMutation(['registerForDrive'], registerForDrive, {
		onSuccess(data) {
			refetch();
			if (isAccountManagerLoggedIn) {
				queryClient.refetchQueries({ queryKey: ['getDriveCompanies'], exact: false });
			}
			queryClient.refetchQueries({ queryKey: ['getDrivePositions'], exact: false });
			navigate(isAccountManagerLoggedIn ? `/drive-positions/${formData?.company}` : '/drive-positions');
			toast({
				title: data?.message || 'Drive registered successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			console.log('error:', error);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const [formData, setFormData] = useState<RecruiterFormData & {
		internshipDuration?: number;
		internshipSalary?: { min: number; max: number };
	}>({
		positionLabel: '',
		positionTitles: [''],
		techStacks: undefined,
		salary: {
			min: 0,
			max: 0,
		},
		company: user?.registeredCompany,
		companyLocation: [],
		internshipDuration: 0,
		internshipSalary: {
			min: 5,
			max: 10,
		},
	});

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (onSubmit) {
			onSubmit(formData);
			return;
		}

		if (isModal) {
			onFinish && onFinish(formData);
		} else {
			registerForDriveFn.mutate({ type: UserRole.RECRUITER, formData, selectedDrives: drives ?? '[]' });
		}
	};

	// Use database position titles if available, otherwise fallback to enum
	useEffect(() => {
		if (positionTitlesData?.lmsTechStacks) {
			const titles = positionTitlesData.lmsTechStacks.map(title => ({
				label: title.label || title.name,
				value: title.name, // Use name (enum value) for backward compatibility
			}));
			setPositionTitles(titles);
		} else {
			// No fallback - only use database values
			setPositionTitles([]);
		}
	}, [positionTitlesData]);

	useEffect(() => {
		const selectableTechStacks = Array.from(
			new Set(
				formData?.positionTitles?.flatMap(posTitle =>
					Object.values(designationMappingWithTechStack[posTitle as PositionTitle] || {}),
				),
			),
		)?.map(ts => ({
			label: generateTechStackName(ts),
			value: ts,
		}));

		setTechStacks(selectableTechStacks);
	}, [formData.positionTitles]);

	return (
		<div
			className={classNames(
				'mx-auto [&_input]:bg-white w-full',
				isModal || existingPositionData ? '' : 'max-w-[580px] my-4 px-4',
			)}
		>
			<form className="bg-white rounded-[12px] p-3 md:p-5 w-full" onSubmit={handleSubmit}>
				{isModal || existingPositionData ? null : (
					<>
						<button
							type="button"
							className="mb-[10px] py-3"
							onClick={() => {
								// navigate('/upcoming-drives');
								navigate('/select-hiring-type');
							}}
						>
							<LiaArrowLeftSolid size={20} />
						</button>
						<div className="flex items-end gap-3 flex-wrap md:flex-nowrap">
							<h2 className="text-xl font-semibold w-fit whitespace-pre">{drive?.city} Offline Placement Drive</h2>
						</div>
						<div className="flex flex-col gap-[8px] w-full mt-2">
							<div className="flex items-start gap-[10px]">
								<HiLocationMarker color="#6E71CC" size={24} className="min-w-[24px] min-h-[24px]" />
								<p className="text-[#3B3435] text-[16px] font-inter font-[400] leading-[24px]">{drive?.location}</p>
							</div>
							<div className="flex items-start gap-[10px]">
								<HiClock color="#6E71CC" size={24} className="min-w-[24px] min-h-[24px]" />
								<p className="text-[#3B3435] text-[16px] font-inter font-[400] leading-[24px]">
									{drive?.driveEndDate && drive?.driveEndDate !== '' && drive?.driveEndDate !== null
										? `${dayjs(drive?.driveDate).format('Do MMM')} - ${dayjs(drive?.driveEndDate).format('Do MMM,')}`
										: `${dayjs(drive?.driveDate).format('Do MMM,')} `}10:00am
								</p>
							</div>
						</div>
					</>
				)}

				<div
					className={classNames(
						'flex items-center gap-4 w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					{isAccountManagerLoggedIn && !onSubmit ? (
						<div className="w-full -mt-3">
							<div className="mb-2 block">
								<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="company" value="Company" />
								<span className="text-red-600"> *</span>
							</div>
							<CompanySelectionWithId
								onChange={data => {
									console.log('data:', data);
									setFormData(prev => ({ ...prev, company: data?.value as CompanyDocument['_id'] }));
								}}
							/>
						</div>
					) : null}
				</div>
				{/* add location */}
				<div className="w-full mb-2">
					<div className="mb-2 block">
						<Label htmlFor="label" value="Work Location" />
					</div>
					<Select
						menuPlacement={'auto'}
						classNamePrefix="flowbite-select"
						className="single-value-select-tag"
						closeMenuOnSelect={true}
						placeholder="Select location"
						isMulti={true}

						options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
						value={formData?.companyLocation?.map(location => ({ value: location, label: location }))}
						onChange={(val: MultiValue<Dropdown>) => {

							setFormData({
								...formData,
								companyLocation: val.map(item => item.value),
							});
						}}

					/>
				</div>
				{/* Position label */}
				<div className="w-full">
					<div className="mb-2 block">
						<Label htmlFor="label" value="Position Label (can be used to differentiate between multiple positions)" />
					</div>
					<TextInput
						color="purple"
						id="label"
						type="text"
						className="RemoveInputArrow [&_input]:pr-[193px]"
						sizing="lg"
						value={formData.positionLabel}
						placeholder={`Position Label`}
						onChange={e => {
							setFormData(prev => ({ ...prev, positionLabel: e.target.value }));
						}}
					/>
				</div>

				<div
					className={classNames(
						'flex items-start gap-4 w-full max-w-[904px]',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap mt-5',
					)}
				>
					<div className="w-full">
						<div className="mb-2 block">
							<Label htmlFor="title" value="Roles hiring" />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							required
							menuPosition="fixed"
							options={positionTitles}
							menuPlacement={'auto'}
							classNamePrefix="flowbite-select"
							value={positionTitles?.filter(e => formData?.positionTitles?.includes(e?.value))}
							onChange={(val: SingleValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, positionTitles: [val?.value ?? ''] }));
							}}
							placeholder="Frontend Developer"
						/>
					</div>

					<div className="w-full">
						<div className="mb-2 block">
							<Label htmlFor="title" value="Must have skills" />
							<span className="text-[10px] font-[500]"> (min 2 skills required)</span>
							<span className="text-red-600">*</span>
						</div>
						<Select
							isMulti
							required
							menuPosition="fixed"
							menuPlacement={'auto'}
							options={skillOptions}
							closeMenuOnSelect={false}
							classNamePrefix="flowbite-select"
							value={skillOptions?.filter(title => formData?.techStacks?.includes(title.value))}
							onChange={(val: MultiValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, techStacks: val.map(item => item.value) }));
							}}
							placeholder="React"
						/>

						{formData?.techStacks && formData?.techStacks?.length < 2 ? (
							<div className="mt-2 text-xs text-red-500">Please choose min of 2 skills</div>
						) : null}
					</div>
				</div>

				<div
					className={classNames(
						'flex items-center gap-4 w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					<div className="w-full">
						<div className="mb-2">
							<Label htmlFor="openings" value="Number of openings" />
							<span className="text-[red]"> *</span>
						</div>
						<TextInput
							color="purple"
							id="openings"
							type="number"
							className="RemoveInputArrow [&_input]:pr-[193px]"
							sizing="lg"
							required
							min={1}
							step="1"
							value={formData.openings}
							placeholder={`Openings`}
							onChange={e => {
								if (Number(e.target.value) <= 0) {
									setFormData(prev => ({ ...prev, openings: undefined }));
									return;
								}

								setFormData(prev => ({ ...prev, openings: Number(e?.target?.value || 0) }));
							}}
						/>
					</div>

					<div className="w-full">
						<div className="mb-2">
							<Label htmlFor="rounds" value="Number of Interview rounds" />
							<span className="text-[red]"> *</span>
						</div>
						<TextInput
							color="purple"
							id="rounds"
							type="number"
							className="RemoveInputArrow [&_input]:pr-[193px]"
							sizing="lg"
							required
							min={1}
							max={10}
							step="1"
							value={formData.rounds}
							placeholder={`Rounds`}
							onChange={e => {
								if (Number(e.target.value) <= 0) {
									setFormData(prev => ({ ...prev, rounds: undefined }));
									return;
								}
								setFormData(prev => ({ ...prev, rounds: Number(e?.target?.value || 0) }));
							}}
						/>
					</div>
				</div>

				<div
					className={classNames(
						'flex items-center gap-4 w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					{isRecruiterLoggedIn ? (
						<div className="w-full">
							<div className="mb-2">
								<Label htmlFor="people" value="Number of people joining drive" />
								<span className="text-[red]"> *</span>
							</div>
							<TextInput
								color="purple"
								id="people"
								type="number"
								className="RemoveInputArrow [&_input]:pr-[193px]"
								sizing="lg"
								required
								min={1}
								step="1"
								value={formData.peopleJoining}
								placeholder={`No. of People Joining`}
								onChange={e => {
									if (Number(e.target.value) <= 0) {
										setFormData(prev => ({ ...prev, peopleJoining: undefined }));
										return;
									}
									setFormData(prev => ({ ...prev, peopleJoining: Number(e?.target?.value || 0) }));
								}}
							/>
						</div>
					) : null}
				</div>

				<div
					className={classNames(
						'flex items-center gap-4 w-full max-w-[904px] mt-5 pb-4',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					<div className="w-full">
						<div className="mb-2">
							<Label htmlFor="jobType" value="Job type" />
							<span className="text-[red]"> *</span>
						</div>
						<fieldset
							className="flex flex-wrap max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setFormData(prev => ({ ...prev, workingMode: target?.value as WorkingMode }));
								}
							}}
						>
							{WorkingMode &&
								Object.values(WorkingMode).map((mode: string, i: number) => (
									<div key={i} className="flex items-center gap-2">
										<Radio
											required={i === 0}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={mode}
											value={mode}
											name="workingMode"
											checked={formData?.workingMode === mode}
											onChange={e => {
												//
											}}
										/>
										<Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
									</div>
								))}
						</fieldset>
					</div>
				</div>

				{/* Conditional fields based on Job type - appear below Job type */}
				<div
					className={classNames(
						'flex items-center gap-4 w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					{/* Internship Duration */}
					{formData?.workingMode &&
						(formData?.workingMode === WorkingMode.INTERNSHIP ||
							formData?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
						<div className="w-full">
							<div className="mb-2">
								<Label htmlFor="internshipDuration" value="Internship Duration (in months)" />
								<span className="text-[red]"> *</span>
							</div>
							<div className="flex items-center gap-[8px]">
								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={
										(formData.internshipDuration !== undefined && formData.internshipDuration <= 1) ||
										formData.internshipDuration === undefined
									}
									onClick={() => {
										setFormData({
											...formData,
											internshipDuration:
												formData.internshipDuration !== undefined ? --formData.internshipDuration : 1,
										});
									}}
								>
									<IoMdRemove className="w-[24px] h-[24px]" />
								</Button>

								<TextInput
									min={1}
									max={12}
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="internshipDuration"
									value={formData.internshipDuration || 0}
									onChange={event => {
										if (+event?.target?.value <= 12)
											setFormData({ ...formData, internshipDuration: +(event?.target?.value || 0) });
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
								/>

								<Button
									className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
									color="light"
									disabled={formData.internshipDuration !== undefined && formData.internshipDuration >= 12}
									onClick={() => {
										setFormData({
											...formData,
											internshipDuration:
												formData.internshipDuration !== undefined ? ++formData.internshipDuration : 1,
										});
									}}
								>
									<IoMdAdd className="w-[24px] h-[24px]" />
								</Button>
							</div>
						</div>
					) : null}

					{/* Internship Salary range */}
					{formData?.workingMode &&
						(formData?.workingMode === WorkingMode.INTERNSHIP ||
							formData?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
						<div className="w-full">
							<div className="mb-2">
								<Label htmlFor="internshipSalary" value="Internship Salary range (per month INR)" />
								<span className="text-[red]"> *</span>
							</div>

							<div className="flex items-center gap-2">
								<TextInput
									min={0}
									max={100}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="0"
									name="internshipSalaryMin"
									value={formData?.internshipSalary?.min || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 100) {
											setFormData({
												...formData,
												internshipSalary: {
													min: +selectedValue,
													max:
														+selectedValue > +(formData?.internshipSalary?.max ?? 0)
															? +selectedValue
															: +(formData?.internshipSalary?.max ?? 0),
												},
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<TextInput
									min={0}
									max={100}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="internshipSalaryMax"
									value={formData?.internshipSalary?.max || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 100 && +selectedValue >= +(formData?.internshipSalary?.min || 0)) {
											setFormData({
												...formData,
												internshipSalary: {
													max: +selectedValue,
													min: +(formData?.internshipSalary?.min ?? 0),
												},
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								K
							</div>
						</div>
					) : null}

					{/* Salary range - Only show when NOT INTERNSHIP */}
					{formData?.workingMode && formData?.workingMode !== WorkingMode.INTERNSHIP && (
						<div className="w-full [&_.MuiSlider-markLabel]:!text-[10px]">
							<div className="mb-2">
								<Label htmlFor="openings" value="Salary range" />
								<span className="text-[10px] font-[500]"> (per annum INR)</span>
								<span className="text-[red]"> *</span>
							</div>

							<div className="flex items-center gap-2">
								<TextInput
									min={0}
									max={80}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="0"
									name="minSalary"
									value={formData?.salary?.min || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 80) {
											setFormData({
												...formData,
												salary: {
													min: +selectedValue,
													max:
														+selectedValue > +formData?.salary?.max
															? +selectedValue
															: +formData?.salary?.max,
												},
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<TextInput
									min={0}
									max={80}
									step="any"
									onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
									type="number"
									placeholder="2"
									name="maxSalary"
									value={formData?.salary?.max || 0}
									onChange={e => {
										const selectedValue = e.target.value || 0;
										if (+selectedValue <= 80 && +selectedValue >= +(formData?.salary?.min || 0)) {
											setFormData({
												...formData,
												salary: { max: +selectedValue, min: formData?.salary?.min },
											});
										}
									}}
									className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								/>
								LPA
							</div>
						</div>
					)}
				</div>

				<div
					className={classNames(
						'bg-white dark:bg-[#111E2C] sticky flex items-center gap-5 bottom-0 mb-[-16px] md:mb-[-20px] mx-[-12px] md:mx-[-20px] px-[16px] md:px-[20px] rounded-b-[12px]',
						!isSticky ? 'border-t' : '',
						isModal ? 'mt-2 md:mt-0 py-3' : 'py-5 mt-3',
					)}
				>
					<Button
						size="lg"
						type="submit"
						color="purplePlaced"
						disabled={registerForDriveFn.isLoading}
						isProcessing={registerForDriveFn.isLoading}
						className={classNames('font-inter w-full ', isModal ? 'max-w-[167px]' : '')}
					>
						{onSubmit ? 'Update' : 'Submit'}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default DriveFormForRecruiter;
